export const API_HOST = process.env.API_HOST;

type Pathable = number | string;

export const fetcher = async (...path: Pathable[]) => {
  const response = await fetch(`/frontend_api/${path.join("/")}.json`);
  return await response.json();
};

async function postRequest<Type>(endpoint: string, params: unknown): Promise<Type> {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  });
  return response.json() as Promise<Type>;
}

async function getRequest<Type>(endpoint: string, options: Record<string, unknown> = {}): Promise<Type> {
  const response = await fetch(endpoint, {
    headers: {
      "Content-Type": "application/json",
    },
    ...options,
  });
  return response.json() as Promise<Type>;
}

export type ValidationErrors<Keys extends string> = {
  errors: Record<Keys | "base", string | string[]>;
};

export type SignInResult =
  | ValidationErrors<"email" | "password">
  | {
      authenticity_token: string;
      redirect_to: string;
      user: User;
    };

export type SignUpResult =
  | ValidationErrors<"name" | "handle" | "email" | "password">
  | {
      authenticity_token: string;
      redirect_to: string;
      user: User;
    };

export type CreateMagicLinkResult =
  | ValidationErrors<"email">
  | {
      message: string;
      email_sent_from: string;
      email_provider: "google" | null;
    };

export type SearchExtensionsResult = {
  data: ExtensionSearchResult[];
  total_results: number;
};

export const signIn = (user: { email: string; password: string }) =>
  postRequest<SignInResult>("/frontend_api/session", { user });

export const signUp = (user: { email: string; name: string; handle: string; password: string }) =>
  postRequest<SignUpResult>("/frontend_api/registrations", { user });

export const createMagicLink = (user: { email: string }) =>
  postRequest<CreateMagicLinkResult>("/frontend_api/magic_links", { user });

type SearchExtensionsParams = {
  searchText: string;
  page?: number;
  options: Record<string, unknown>;
};

export const searchExtensions = ({ searchText, page = 1, options = {} }: SearchExtensionsParams) =>
  getRequest<SearchExtensionsResult>(
    `/frontend_api/extensions/search?q=${encodeURIComponent(searchText)}&page=${page}`,
    options
  );

export type User = {
  name: string;
  handle: string;
  bio: null | string;
  twitter_handle: null | string;
  github_handle: null | string;
  location: null | string;
  website: null | string;
  initials: string;
  avatar_placeholder_color: string;
  username: string;
  avatar: string | string;
};

export type Organization = {
  avatar: string;
  name: string;
  handle: string;
};

export type Command = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  name: string;
  icons: {
    light?: string;
    dark?: string;
  };
  keywords: string[];
};

export type Extension = {
  id: string;
  title: string;
  name: string;
  icons: {
    light: string;
    dark: string;
  };
  author: User;
  owner: User | Organization;
  description: string;
  commands: Command[];
  private: boolean;
  download_count: number;
  contributors: User[];
  categories: string[];
  source_url?: string;
  report_bug_url?: string;
  request_feature_url?: string;
  native_id?: string;
  readme?: string;
  store_url: string;
};

export type ExtensionSearchResult = Extension & {
  links: {
    self: string;
    owner: string;
  };
};

export type ChangelogEntry = {
  title: string;
  version: string;
  body: string;
  date: string;
  slug: string;
};

export function fetchLatestRaycastVersion() {
  return fetch("https://api.raycast.com/v2/changelog/latest", {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${process.env.NEXT_PUBLIC_RAYCAST_API_TOKEN}`,
    },
  }).then((response) => response.json());
}

const slugifyVersion = (version: string) => version.replace(/\./g, "-");

// Adds `/>` to all <img> tags without closing slash
// because MDX doesn't support unclosed html tags
const fixImgTags = (markdown: string): string => markdown.replace(/<img([^>]*)[^\/]?>/g, "<img$1/>");

export async function fetchChangelogEntries() {
  const response = await fetch("https://api.raycast.com/v2/changelog", {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${process.env.NEXT_PUBLIC_RAYCAST_API_TOKEN}`,
    },
  });
  const entries = (await response.json()) as ChangelogEntry[];

  return entries.map((changelogEntry) => ({
    ...changelogEntry,
    body: changelogEntry.body ? fixImgTags(changelogEntry.body) : null,
    slug: slugifyVersion(changelogEntry.version),
  })) as ChangelogEntry[];
}
